import React, { useState, useEffect } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend
} from 'recharts';
import apiKeyService from '../services/apiKeyService';

const PROVIDER_COLORS = {
  groq: '#10B981',
  gemini: '#3B82F6',
  anthropic: '#8B5CF6',
  openai: '#F59E0B'
};

const UsageChart = () => {
  const [chartData, setChartData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(14);
  const [chartType, setChartType] = useState('area'); // 'area' | 'bar'

  useEffect(() => {
    loadStats();
  }, [days]);

  const loadStats = async () => {
    setLoading(true);
    try {
      const result = await apiKeyService.getDailyStats(days);
      if (result.success) {
        // 차트 데이터 변환
        const formattedData = result.stats.chartData.map(d => ({
          date: formatDate(d.date),
          fullDate: d.date,
          total: d.total,
          success: d.success,
          fail: d.fail,
          groq: d.byProvider.groq || 0,
          gemini: d.byProvider.gemini || 0,
          anthropic: d.byProvider.anthropic || 0,
          openai: d.byProvider.openai || 0
        }));
        setChartData(formattedData);
        setSummary(result.stats.summary);
      }
    } catch (err) {
      console.error('Failed to load stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white border border-gray-200 rounded-md p-4 shadow-lg min-w-[200px]">
          <p className="text-xs font-bold text-gray-900 m-0 mb-2 pb-2 border-b border-gray-200 tracking-wide">{data.fullDate}</p>
          <p className="text-[15px] font-bold text-blue-500 m-0 mb-1">총 {data.total}회</p>
          {data.groq > 0 && <p className="text-xs text-emerald-500 my-1 font-medium">Groq: {data.groq}</p>}
          {data.gemini > 0 && <p className="text-xs text-blue-500 my-1 font-medium">Gemini: {data.gemini}</p>}
          {data.anthropic > 0 && <p className="text-xs text-violet-500 my-1 font-medium">Anthropic: {data.anthropic}</p>}
          {data.openai > 0 && <p className="text-xs text-amber-500 my-1 font-medium">OpenAI: {data.openai}</p>}
          {data.fail > 0 && <p className="text-xs text-red-500 mt-2 pt-2 border-t border-dashed border-gray-200 font-medium">실패: {data.fail}</p>}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-6 mb-8 shadow-sm flex items-center justify-center min-h-[200px] text-gray-600">
        차트 로딩 중...
      </div>
    );
  }

  const hasData = chartData.some(d => d.total > 0);

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 mb-8 shadow-sm max-sm:p-4">
      <div className="flex justify-between items-start mb-6 gap-4 flex-wrap max-sm:flex-col max-sm:gap-4">
        <div>
          <h4 className="text-[15px] font-bold m-0 mb-1 text-gray-900 tracking-tight">API 사용량 추이</h4>
          {summary && (
            <div className="flex gap-4 flex-wrap max-sm:flex-col max-sm:gap-1">
              <span className="text-xs text-gray-600 leading-relaxed">
                <span className="mr-1">총 호출:</span>
                <strong className="text-gray-900 font-medium">{summary.totalCalls.toLocaleString()}</strong>
              </span>
              <span className="text-xs text-gray-600 leading-relaxed">
                <span className="mr-1">일 평균:</span>
                <strong className="text-gray-900 font-medium">{summary.avgPerDay}</strong>
              </span>
              {summary.peakDay && (
                <span className="text-xs text-gray-600 leading-relaxed">
                  <span className="mr-1">최대:</span>
                  <strong className="text-gray-900 font-medium">{summary.peakDay.count} ({formatDate(summary.peakDay.date)})</strong>
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex gap-2 max-sm:w-full max-sm:justify-between">
          <div className="flex bg-slate-50 rounded-md p-1 border border-gray-200">
            {[7, 14, 30].map((d) => (
              <button
                key={d}
                className={`py-2 px-4 border-none bg-transparent text-xs font-medium text-gray-600 cursor-pointer rounded-md transition-all whitespace-nowrap max-sm:py-2 max-sm:px-4 max-sm:text-[11px] ${
                  days === d
                    ? 'bg-white text-blue-500 shadow-sm font-bold'
                    : 'hover:text-gray-900'
                }`}
                onClick={() => setDays(d)}
              >
                {d}일
              </button>
            ))}
          </div>
          <div className="flex bg-slate-50 rounded-md p-1 border border-gray-200">
            <button
              className={`py-2 px-4 border-none bg-transparent text-xs font-medium text-gray-600 cursor-pointer rounded-md transition-all whitespace-nowrap ${
                chartType === 'area'
                  ? 'bg-white text-blue-500 shadow-sm font-bold'
                  : 'hover:text-gray-900'
              }`}
              onClick={() => setChartType('area')}
              title="영역 차트"
            >
              📈
            </button>
            <button
              className={`py-2 px-4 border-none bg-transparent text-xs font-medium text-gray-600 cursor-pointer rounded-md transition-all whitespace-nowrap ${
                chartType === 'bar'
                  ? 'bg-white text-blue-500 shadow-sm font-bold'
                  : 'hover:text-gray-900'
              }`}
              onClick={() => setChartType('bar')}
              title="막대 차트"
            >
              📊
            </button>
          </div>
        </div>
      </div>

      {!hasData ? (
        <div className="flex flex-col items-center justify-center min-h-[200px] text-center text-gray-600 bg-slate-50 rounded-md p-8">
          <p className="m-1 leading-relaxed text-[15px] font-medium text-gray-900">아직 API 사용 기록이 없습니다.</p>
          <p className="m-1 leading-relaxed text-xs opacity-70">API 키를 등록하고 사용하면 여기에 통계가 표시됩니다.</p>
        </div>
      ) : (
        <div className="w-full h-[280px] py-2 max-sm:h-[240px]">
          <ResponsiveContainer width="100%" height={280}>
            {chartType === 'area' ? (
              <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fill: '#6B7280' }}
                  tickLine={false}
                  axisLine={{ stroke: '#E5E7EB' }}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#6B7280' }}
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="total"
                  stroke="#3B82F6"
                  strokeWidth={2}
                  fill="url(#colorTotal)"
                />
              </AreaChart>
            ) : (
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fill: '#6B7280' }}
                  tickLine={false}
                  axisLine={{ stroke: '#E5E7EB' }}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#6B7280' }}
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  iconType="circle"
                  iconSize={8}
                  wrapperStyle={{ fontSize: 11 }}
                />
                <Bar dataKey="groq" stackId="a" fill={PROVIDER_COLORS.groq} name="Groq" />
                <Bar dataKey="gemini" stackId="a" fill={PROVIDER_COLORS.gemini} name="Gemini" />
                <Bar dataKey="anthropic" stackId="a" fill={PROVIDER_COLORS.anthropic} name="Anthropic" />
                <Bar dataKey="openai" stackId="a" fill={PROVIDER_COLORS.openai} name="OpenAI" />
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>
      )}

      {/* 프로바이더별 범례 */}
      {hasData && chartType === 'area' && (
        <div className="flex justify-center gap-4 mt-4 pt-4 border-t border-gray-200 flex-wrap max-sm:justify-start max-sm:gap-4">
          <span className="flex items-center text-xs text-gray-600 font-medium gap-2 max-sm:text-[11px]">
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: PROVIDER_COLORS.groq }}></span>
            Groq
          </span>
          <span className="flex items-center text-xs text-gray-600 font-medium gap-2 max-sm:text-[11px]">
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: PROVIDER_COLORS.gemini }}></span>
            Gemini
          </span>
          <span className="flex items-center text-xs text-gray-600 font-medium gap-2 max-sm:text-[11px]">
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: PROVIDER_COLORS.anthropic }}></span>
            Anthropic
          </span>
          <span className="flex items-center text-xs text-gray-600 font-medium gap-2 max-sm:text-[11px]">
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: PROVIDER_COLORS.openai }}></span>
            OpenAI
          </span>
        </div>
      )}
    </div>
  );
};

export default UsageChart;
